import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, ListGroup } from 'react-bootstrap';
import { type IProduct } from '../../types/IProduct';
import { useAppDispatch } from '../../store/reduxHooks';
import { resetProduct } from '../../store/product';
import { formatCurrency } from '../../utils/formatCurrency';
import { getImage } from '../../utils/getImage';
import { HiddenIcon } from '../HiddenIcon/HiddenIcon';

interface ProductCardProps {
  product: IProduct;
}

export const ProductCard: React.FC<ProductCardProps> = memo(({ product }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const productImageLink = useMemo(() => {
    return getImage(product.image);
  }, [product.image]);

  const handleCardClick = useCallback(() => {
    void navigate(`/product/${product.id}`);
    dispatch(resetProduct());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const offersPriceRange = useMemo(() => {
    if (
      product.offers === undefined ||
      product.offers === null ||
      product.offers.length === 0
    ) {
      return null;
    }

    const offerPrices: number[] = [];

    product.offers.forEach((offer) => {
      if (typeof offer.akcia === 'number') {
        offerPrices.push(offer.akcia);
      } else if (typeof offer.price === 'number') {
        offerPrices.push(offer.price);
      } else if (typeof offer.price_sale === 'number') {
        offerPrices.push(offer.price_sale);
      }
    });
    if (offerPrices.length === 0) {
      return null;
    }
    const priceRange = {
      minOfferPrice: Math.min(...offerPrices),
      maxOfferPrice: Math.max(...offerPrices)
    };
    return priceRange;
  }, [product.offers]);

  return (
    <Card
      style={{ width: '20rem' }}
      className="shadow p-3 mb-3 bg-white rounded pe-auto custom-pointer"
      onClick={handleCardClick}
    >
      <div style={{ position: 'relative' }}>
        <Card.Img variant="top" src={productImageLink} />
        <HiddenIcon
          isHidden={product.hidden}
          tooltipText="Товар скрыт"
          iconSize={28}
          iconColor="red"
          customClassName="card-hidden-icon"
        />
      </div>
      <Card.Body>
        <Card.Title>{product.name}</Card.Title>
        <Card.Text>
          {(product.description !== null ||
            product.description !== undefined) &&
          product.description.length > 0 ? (
            <span className="custom-text-truncate-3-lines text-muted">
              {product.description}
            </span>
          ) : (
            <span className="text-danger">Описание товара отсутствует</span>
          )}
        </Card.Text>
        <ListGroup className="list-group-flush">
          <ListGroup.Item>
            <span>
              <strong>Категория:</strong>
            </span>{' '}
            {(product?.category !== null || product?.category !== undefined) &&
            product?.category.name.length > 0 ? (
              <span>{product.category.name}</span>
            ) : (
              <span className="text-danger">Категория не указана</span>
            )}
          </ListGroup.Item>
          <ListGroup.Item>
            <span>
              <strong>Артикул:</strong>
            </span>{' '}
            <span>{product.vendorCode}</span>
          </ListGroup.Item>
          {product.brands.length > 0 && (
            <ListGroup.Item>
              {' '}
              <span>
                <strong>
                  {product.brands.length > 1 ? 'Бренды:' : 'Бренд:'}
                </strong>
              </span>{' '}
              {product.brands.map((brand, index) => {
                const isLast = product.brands.length - 1 === index;
                return (
                  <span key={index}>
                    {brand.brand.name}
                    {!isLast ? ',' : ''}
                  </span>
                );
              })}
            </ListGroup.Item>
          )}
          {offersPriceRange !== null && (
            <ListGroup.Item>
              <span>
                <strong>Минимальная цена предложения:</strong>
              </span>{' '}
              <span>
                <strong className="text-muted">
                  {formatCurrency(offersPriceRange.minOfferPrice)}
                </strong>
              </span>
            </ListGroup.Item>
          )}
          {offersPriceRange !== null && (
            <ListGroup.Item>
              <span>
                <strong>Максимальная цена предложения:</strong>
              </span>{' '}
              <span>
                <strong className="text-muted">
                  {formatCurrency(offersPriceRange.maxOfferPrice)}
                </strong>
              </span>
            </ListGroup.Item>
          )}
        </ListGroup>
      </Card.Body>
    </Card>
  );
});

ProductCard.displayName = 'ProductCard';
